import { useEffect, useId, useMemo, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { researchCategoryClasses } from "#shared/researchCategoryColors.ts";
import type { ResearchCategory } from "#shared/schemas/research.ts";
import { cn } from "@web/lib/utils.ts";

function normalizeCategoryInput(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/g, "_");
}

/**
 * Pick an existing research category or type a new one.
 * Shares the same vocabulary as prompt angles / ingested research cards.
 */
export function CategoryCombobox({
  value,
  categories,
  disabled,
  allowClear = true,
  autoOpen = false,
  "aria-label": ariaLabel,
  onChange,
  onDismiss,
}: {
  value: string | null;
  categories: readonly ResearchCategory[];
  disabled?: boolean;
  allowClear?: boolean;
  /** Open immediately (e.g. when chip was clicked to edit). */
  autoOpen?: boolean;
  "aria-label": string;
  onChange: (next: string | null) => void;
  onDismiss?: () => void;
}) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(autoOpen);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);

  const names = useMemo(
    () => categories.map((c) => c.name).sort((a, b) => a.localeCompare(b)),
    [categories],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return names;
    return names.filter((n) => n.includes(q) || n.replaceAll("_", " ").includes(q));
  }, [names, query]);

  const createName = normalizeCategoryInput(query);
  const canCreate =
    createName.length > 0 && !names.some((n) => n.toLowerCase() === createName);

  const rows = useMemo(() => {
    const list: { kind: "clear" | "pick" | "create"; name: string | null }[] = [];
    if (allowClear) list.push({ kind: "clear", name: null });
    for (const name of filtered) list.push({ kind: "pick", name });
    if (canCreate) list.push({ kind: "create", name: createName });
    return list;
  }, [allowClear, filtered, canCreate, createName]);

  useEffect(() => {
    if (!open) return;
    setHighlight(0);
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
        onDismiss?.();
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open, onDismiss]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  function pick(next: string | null) {
    onChange(next);
    setOpen(false);
    setQuery("");
  }

  function closeMenu() {
    setOpen(false);
    setQuery("");
    onDismiss?.();
  }

  function openMenu() {
    if (disabled) return;
    setQuery("");
    setOpen(true);
  }

  const colors = researchCategoryClasses(value);

  return (
    <div ref={rootRef} className="relative min-w-40">
      {open ? (
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded
          aria-controls={listId}
          aria-autocomplete="list"
          aria-label={ariaLabel}
          disabled={disabled}
          value={query}
          placeholder="Search or create…"
          onChange={(e) => {
            setQuery(e.target.value);
            setHighlight(0);
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              closeMenu();
              return;
            }
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setHighlight((h) => Math.min(h + 1, Math.max(0, rows.length - 1)));
              return;
            }
            if (e.key === "ArrowUp") {
              e.preventDefault();
              setHighlight((h) => Math.max(h - 1, 0));
              return;
            }
            if (e.key === "Enter") {
              e.preventDefault();
              const row = rows[highlight];
              if (!row) return;
              if (row.kind === "clear") pick(null);
              else if (row.name) pick(row.name);
            }
          }}
          className="h-8 w-full rounded-md border border-line bg-panel px-2 text-sm text-text placeholder:text-faint focus-visible:outline-none"
        />
      ) : (
        <button
          type="button"
          disabled={disabled}
          aria-label={ariaLabel}
          aria-haspopup="listbox"
          onClick={openMenu}
          className={cn(
            "inline-flex h-8 w-full items-center justify-between gap-2 rounded-md border px-2 text-left text-sm",
            "border-line bg-panel hover:border-muted disabled:opacity-50",
          )}
        >
          {value ? (
            <span
              className={cn(
                "truncate rounded-sm border px-1.5 py-0.5 text-xs font-semibold tracking-wider uppercase",
                colors.text,
                colors.bg,
                colors.border,
              )}
            >
              {value}
            </span>
          ) : (
            <span className="text-faint">Category…</span>
          )}
          <ChevronDown className="size-3.5 shrink-0 text-faint" />
        </button>
      )}

      {open && (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-line bg-panel-elevated py-1 shadow-focus"
        >
          {rows.length === 0 && (
            <li className="px-3 py-2 text-sm text-faint">No categories</li>
          )}
          {rows.map((row, i) => {
            const active = i === highlight;
            if (row.kind === "clear") {
              return (
                <li key="__clear" role="option" aria-selected={active}>
                  <button
                    type="button"
                    className={cn(
                      "w-full px-3 py-1.5 text-left text-sm text-muted",
                      active && "bg-row-hover text-text",
                    )}
                    onMouseEnter={() => setHighlight(i)}
                    onClick={() => pick(null)}
                  >
                    No category
                  </button>
                </li>
              );
            }
            if (row.kind === "create" && row.name) {
              return (
                <li key={`create:${row.name}`} role="option" aria-selected={active}>
                  <button
                    type="button"
                    className={cn(
                      "w-full px-3 py-1.5 text-left text-sm",
                      active ? "bg-row-hover text-text" : "text-accent",
                    )}
                    onMouseEnter={() => setHighlight(i)}
                    onClick={() => pick(row.name)}
                  >
                    Create “{row.name}”
                  </button>
                </li>
              );
            }
            const chip = researchCategoryClasses(row.name);
            return (
              <li key={row.name} role="option" aria-selected={active}>
                <button
                  type="button"
                  className={cn(
                    "flex w-full items-center gap-2 px-3 py-1.5 text-left",
                    active && "bg-row-hover",
                  )}
                  onMouseEnter={() => setHighlight(i)}
                  onClick={() => pick(row.name)}
                >
                  <span
                    className={cn(
                      "rounded-sm border px-1.5 py-0.5 text-xs font-semibold tracking-wider uppercase",
                      chip.text,
                      chip.bg,
                      chip.border,
                    )}
                  >
                    {row.name}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
